import { TypographyVariantsOptions, Palette } from '@mui/material/styles';
import DHI_COLORS from './colors';

const FONT_FAMILY = [
  'Roboto',
  '-apple-system',
  'BlinkMacSystemFont',
  'Arial',
  'sans-serif',
].join(',');

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    display1: true;
    display2: true;
    display3: true;
    display4: true;
    body0: true;
    body3: true;
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    display1: TypographyVariantsOptions;
    display2: TypographyVariantsOptions;
    display3: TypographyVariantsOptions;
    display4: TypographyVariantsOptions;
    body0: TypographyVariantsOptions;
    body3: TypographyVariantsOptions;
  }

  interface TypographyVariantsOptions {
    display1?: React.CSSProperties & { [key: string]: any };
    display2?: React.CSSProperties & { [key: string]: any };
    display3?: React.CSSProperties & { [key: string]: any };
    display4?: React.CSSProperties & { [key: string]: any };
    body0?: React.CSSProperties & { [key: string]: any };
    body3?: React.CSSProperties & { [key: string]: any };
  }
}

export const DHITypographyV516:
  | TypographyVariantsOptions
  | ((palette: Palette) => TypographyVariantsOptions) = {
  htmlFontSize: 16,
  fontSize: 16,
  fontFamily: FONT_FAMILY,
  display1: {
    fontSize: '3.1875rem',
    fontWeight: 600,
    lineHeight: 1.16,

    '@media (max-width:1300px)': {
      fontSize: '2.8125rem',
      lineHeight: 1,
    },

    '@media (max-width:600px)': {
      fontSize: '2.25rem',
      lineHeight: 1.33,
    },
  },
  display2: {
    fontSize: '2.8125rem',
    fontWeight: 600,
    lineHeight: 1.1,

    '@media (max-width:1300px)': {
      fontSize: '2.25rem',
      lineHeight: 1.33,
    },

    '@media (max-width:600px)': {
      fontSize: '2rem',
      lineHeight: 1.25,
    },
  },
  display3: {
    fontSize: '2.25rem',
    fontWeight: 600,
    lineHeight: 1.11,

    '@media (max-width:1300px)': {
      fontSize: '2rem',
      lineHeight: 1.25,
    },

    '@media (max-width:600px)': {
      fontSize: '1.75rem',
      lineHeight: 1.35,
    },
  },
  display4: {
    fontSize: '2.125rem',
    fontWeight: 400,
    lineHeight: 1.11,
    color: DHI_COLORS.BLACK,

    '@media (max-width:1300px)': {
      fontSize: '2rem',
      lineHeight: 1.25,
    },

    '@media (max-width:600px)': {
      fontSize: '1.75rem',
      lineHeight: 1.35,
    },
  },
  h1: {
    fontSize: '2rem',
    fontWeight: 500,
    lineHeight: 1.1875,
    color: DHI_COLORS.BLACK,

    '@media (max-width:1300px)': {
      fontSize: '1.75rem',
      lineHeight: 1.35,
    },

    '@media (max-width:600px)': {
      fontSize: '1.5625rem',
      lineHeight: 1.36,
    },
  },
  h2: {
    fontSize: '1.75rem',
    fontWeight: 500,
    lineHeight: 1.21,
     color: DHI_COLORS.BLACK,

    '@media (max-width:1300px)': {
      fontSize: '1.5625rem',
      lineHeight: 1.36,
    },

    '@media (max-width:600px)': {
      fontSize: '1.375rem',
      lineHeight: 1.63,
    },
  },
  h3: {
    fontSize: '1.5rem',
    fontWeight: 500,
    lineHeight: 1.44,
    color: DHI_COLORS.BLACK_NGDI_EXTRA_DARK,

    '@media (max-width:1300px)': {
      fontSize: '1.375rem',
      lineHeight: 1.63,
    },

    '@media (max-width:600px)': {
      fontSize: '1.25rem',
      lineHeight: 1.8,
    },
  },
  h4: {
    fontSize: '1.375rem',
    fontWeight: 500,
    lineHeight: 1.63,
    color: DHI_COLORS.BLACK,

    '@media (max-width:1300px)': {
      fontSize: '1.25rem',
      lineHeight: 1.8,
    },

    '@media (max-width:600px)': {
      fontSize: '1.125rem',
      lineHeight: 1.55,
    },
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.4,
    color: DHI_COLORS.BLACK,

    '@media (max-width:1300px)': {
      fontSize: '1.125rem',
      lineHeight: 1.55,
    },

    '@media (max-width:600px)': {
      fontSize: '1rem',
      fontHeight: 1.5,
    },
  },
  h6: {
    fontSize: '1.125rem',
    fontWeight: 500,
    lineHeight: 1.33,
    color: DHI_COLORS.BLACK_NGDI_EXTRA_DARK,

    '@media (max-width:1300px)': {
      fontSize: '1rem',
      fontHeight: 1.5,
    },

    '@media (max-width:600px)': {
      fontSize: '0.875rem',
      fontHeight: 1.5,
    },
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: 1.5,
    color: DHI_COLORS.BLACK,
    
    '@media (max-width:1300px)': {
      fontSize: '0.9rem',
      fontHeight: 1.5,
    },

    '@media (max-width:600px)': {
      fontSize: '0.75rem',
      fontHeight: 1.43,
    },
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.43,

    '@media (max-width:1300px)': {
      fontSize: '0.75rem',
      fontHeight: 1.43,
    },

    '@media (max-width:600px)': {
      fontSize: '0.675rem',
      fontHeight: 1.43,
    },
  },
  body0: {
    fontSize: '1.125rem',
    fontWeight: 400,
    lineHeight: 1.5,

    '@media (max-width:1300px)': {
      fontSize: '1rem',
      fontHeight: 1.5,
    },
  },
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.5,

    '@media (max-width:1300px)': {
      fontSize: '0.875rem',
      fontHeight: 1.5,
    },

    '@media (max-width:600px)': {
      fontSize: '0.75rem',
      fontHeight: 1.45,
    },
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.33,

    '@media (max-width:1300px)': {
      fontSize: '0.75rem',
      fontHeight: 1.45,
    },

    '@media (max-width:600px)': {
      fontSize: '0.6875rem',
      fontHeight: 1.56,
    },
  },
  body3: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.43,

    '@media (max-width:1300px)': {
      fontSize: '0.6875rem',
      fontHeight: 1.56,
    },

    '@media (max-width:600px)': {
      fontSize: '0.6125rem',
      fontHeight: 1.56,
    },
  },
  caption: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.66,

    '@media (max-width:1300px)': {
      fontSize: '0.6875rem',
      fontHeight: 1.66,
    },

    '@media (max-width:600px)': {
      fontSize: '0.6125rem',
      fontHeight: 1.66,
    },
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 2.66,

    '@media (max-width:1300px)': {
      fontSize: '0.6875rem',
      fontHeight: 2.66,
    },

    '@media (max-width:600px)': {
      fontSize: '0.6125rem',
      fontHeight: 2.66,
    },
  },
};
